import {
  clipsNeedingConfigUpdateStore,
  currentClip,
  saveClipChanges,
  isScreenShareEnabledForCustomUploadClipsFeatureFlag,
  updateClipWithId,
  updateVisibleFaceIdsInClip
} from '../clip';
import createStore from '../store';
import {
  SpeakersAnalysis,
  SpeakersAnalysisStore,
  ScreenshareBound,
  VisionApiScreenshareResponse
} from './speakersAnalysisTypes';
import { findAdjustedBoxBoundaries } from './speakersAnalysisUtils';
import { getEnv } from '@/libs/env';
import { AppContextState } from '@/context/AppContext/AppContextTypes';
import { roundToNDecimalPlaces, timeStringToSeconds } from '@/libs/utils';

export const defaultSpeakersAnalysis: SpeakersAnalysis = {
  face_ids: [],
  time_analysis: [],
  times: [],
  visible_face_ids: [],
  speaker_mapping: {},
  loading: true,
  adjustedBoxBoundaries: [],
  active_speaker_mapping: {}
};

export function initSpeakersAnalysisStore(clipId: string) {
  updateSpeakersAnalysisStoreWithId(clipId, defaultSpeakersAnalysis);
}

export const speakersAnalysisStore = createStore<SpeakersAnalysisStore>({});

export function getSpeakersAnalysis(
  id: string,
  start: number,
  visible_face_ids: number[],
  isAnalysisAccurate?: boolean,
  onAsdComplete?: (clipId: string) => void
): Promise<void> {
  return fetch(`${getEnv('VISION_URL')}/analysis/clips/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${window.accessToken}`
    },
    mode: 'cors'
  })
    .then(res => {
      if (res.status === 200) {
        return res.json();
      }
      throw new Error('Error fetching speakers analysis');
    })
    .then(
      (
        data: Pick<
          SpeakersAnalysis,
          'accurate' | 'time_analysis' | 'face_ids' | 'speaker_mapping' | 'active_speaker_mapping'
        > & {
          screenshares?: VisionApiScreenshareResponse[];
        }
      ) => {
        const {
          accurate,
          face_ids = [],
          time_analysis = [],
          speaker_mapping = {},
          active_speaker_mapping = {},
          screenshares
        } = data;

        if (isAnalysisAccurate === false && accurate === true) {
          clipsNeedingConfigUpdateStore.update(store => ({
            ...store,
            [id]: true
          }));
        }

        // Math.floor logic to round time to 5 decimal places
        const updatedTimes = time_analysis.map(t => roundToNDecimalPlaces(timeStringToSeconds(t.time) + start, 4));

        const adjustedBoxBoundaries = findAdjustedBoxBoundaries(time_analysis);

        // Parse screenshare bounds from API response (only if feature flag enabled)
        const shouldProcessScreenshare = isScreenShareEnabledForCustomUploadClipsFeatureFlag();

        let screenshare_bounds: ScreenshareBound[] | undefined;

        if (shouldProcessScreenshare && screenshares && screenshares.length > 0) {
          screenshare_bounds = screenshares.map(screenshare => ({
            start_time: roundToNDecimalPlaces(screenshare.start_time + start, 4),
            end_time: roundToNDecimalPlaces(screenshare.end_time + start, 4),
            coordinates: {
              x: screenshare.coordinates.x,
              y: screenshare.coordinates.y,
              width: screenshare.coordinates.width,
              height: screenshare.coordinates.height
            }
          }));
        }

        // Only mark loading as done once face rec data has arrived.
        const isFaceRecDone = time_analysis.length > 0;

        updateSpeakersAnalysisStoreWithId(id, {
          accurate,
          face_ids: face_ids,
          time_analysis,
          times: updatedTimes,
          visible_face_ids: visible_face_ids || face_ids.slice(0, 4),
          speaker_mapping,
          loading: !isFaceRecDone,
          adjustedBoxBoundaries,
          active_speaker_mapping,
          screenshare_bounds
        });

        if (accurate === true && Object.keys(active_speaker_mapping).length > 0) {
          onAsdComplete?.(id);
        }
      }
    );
}

const MAX_DURATION_TO_POLL = 2 * 60 * 1000;
export const VISION_API_POLL_INTERVAL = 3000;

export function pollForSpeakerAnalysis(
  clipId: string,
  start: number,
  visible_face_ids: number[],
  logger: AppContextState['logger'],
  maxDurationOverride?: number,
  isAnalysisAccurate?: boolean,
  onAsdComplete?: (clipId: string) => void
) {
  let pollDuration = 0;
  const intervalId = setInterval(() => {
    pollDuration += VISION_API_POLL_INTERVAL;
    if (pollDuration > (maxDurationOverride || MAX_DURATION_TO_POLL)) {
      logger.warn('Speaker analysis polling duration exceeded');
      clearInterval(intervalId);
      return;
    }

    const doesSpeakerAnalysisExistForClip = speakersAnalysisStore.getSnapshot()[clipId]?.times.length > 0;
    getSpeakersAnalysis(clipId, start, visible_face_ids, isAnalysisAccurate, onAsdComplete)
      .then(() => {
        // Only stop polling when face rec data has arrived (times populated).
        const hasFaceRecData = speakersAnalysisStore.getSnapshot()[clipId]?.times.length > 0;
        if (!hasFaceRecData) {
          return;
        }

        if (intervalId) {
          clearInterval(intervalId);
        }

        // This is to ensure that the clip updates after the clip has been extended
        if (doesSpeakerAnalysisExistForClip) {
          const clipData = currentClip.getSnapshot()[clipId];
          updateClipWithId(clipId, {
            asset_metadata: clipData.asset_metadata
          });
          saveClipChanges({
            clipId,
            shouldGenerateThumbnail: false,
            shouldAddToHistory: false,
            isEditedState: false
          });
        }
      })
      .catch(() => {
        const speakersAnalysisForClip = speakersAnalysisStore.getSnapshot()[clipId];

        if (speakersAnalysisForClip?.times.length !== 0) {
          updateSpeakersAnalysisStoreWithId(clipId, defaultSpeakersAnalysis);
        }
      });
  }, VISION_API_POLL_INTERVAL);

  return intervalId;
}

export function updateVisibleFaceIds(clipId: string, ids: number[]) {
  updateSpeakersAnalysisStoreWithId(clipId, { visible_face_ids: ids });

  updateVisibleFaceIdsInClip(clipId, ids);
}

export function updateSpeakersAnalysisStoreWithId(clipId: string, data: Partial<SpeakersAnalysis>) {
  speakersAnalysisStore.update(store => ({
    ...store,
    [clipId]: {
      ...store[clipId],
      ...data
    }
  }));
}
